import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { PillButton } from '../ui';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { ReactComponent as LogoMark } from '../assets/icons/shield.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as HomeIcon } from '../assets/icons/home.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as ActivityIcon } from '../assets/icons/activity.svg';
import { ReactComponent as ShareIcon } from '../assets/icons/share.svg';
import { ReactComponent as MessageCircleIcon } from '../assets/icons/message-circle.svg';
import { ReactComponent as UserIcon } from '../assets/icons/user.svg';
import { ReactComponent as OfficeIcon } from '../assets/icons/office-building.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as BellIcon } from '../assets/icons/bell.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as MenuIcon } from '../assets/icons/menu.svg';
import { ReactComponent as LogOutIcon } from '../assets/icons/log-out.svg';
import './AppShell.css';
import { adoptAccountLanguage } from '../i18n/language';

/*
 * CONTRATO DEL SHELL
 * ------------------
 * Para añadir una pantalla nueva sobre este shell:
 *   1. Crea el componente de página en client/src/pages/<Nombre>.js.
 *   2. Añade una <Route path="/tu-ruta" element={<TuPagina />} /> dentro del
 *      <Route element={<AppShell .../>}> de App.js (es una layout route de
 *      react-router: todo lo que cuelgue de ahi se pinta en el <Outlet/> de aqui).
 *   3. Si la pantalla debe aparecer en la sidebar/bottom nav, añade una entrada
 *      a NAV_ITEMS más abajo: { to, icon, labelKey, showInBottomNav }.
 * Nada más: el shell no necesita tocarse para que la pagina funcione.
 */
export const NAV_ITEMS = [
  { to: '/', icon: HomeIcon, labelKey: 'nav.home', end: true, showInBottomNav: true },
  { to: '/2fa', icon: ShieldIcon, labelKey: 'nav.twoFactor', showInBottomNav: true },
  { to: '/health', icon: ActivityIcon, labelKey: 'nav.health', showInBottomNav: true },
  { to: '/messages', icon: MessageCircleIcon, labelKey: 'nav.messages', showInBottomNav: false },
  { to: '/share', icon: ShareIcon, labelKey: 'nav.share', showInBottomNav: false },
  { to: '/account', icon: UserIcon, labelKey: 'nav.account', showInBottomNav: true },
];

const ADMIN_ITEM = { to: '/admin', icon: OfficeIcon, labelKey: 'nav.admin', showInBottomNav: false };

function AppShell({ onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [isOrgMember, setIsOrgMember] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { count: unreadMessages } = useUnreadMessages();
  const [security, setSecurity] = useState(null);

  // Estado de PIN/contraseña maestra: se revisa en cada cambio de ruta para
  // que el aviso desaparezca en cuanto se resuelva desde /account/security,
  // sin necesidad de un contexto global para un solo banner.
  useEffect(() => {
    let cancelled = false;
    api.get('/account/security')
      .then((res) => {
        if (cancelled) return;
        setSecurity(res.data);
        // Idioma de la cuenta: se adopta si la persona no eligio otro en este navegador.
        adoptAccountLanguage(res.data?.language);
      })
      .catch(() => { if (!cancelled) setSecurity(null); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  const needsSecurityFactor = Boolean(
    security && location.pathname !== '/account/security'
      && (security.mustSetFactor || (!security.hasPin && !security.hasMasterPassword)),
  );

  useEffect(() => {
    let cancelled = false;
    api.get('/org/mine')
      .then((res) => {
        if (!cancelled) setIsOrgMember((res.data?.organizations?.length || 0) > 0);
      })
      .catch(() => { /* sin org o sin permiso: se oculta Admin, no es un error del usuario */ });
    return () => { cancelled = true; };
  }, []);

  const items = isOrgMember ? [...NAV_ITEMS, ADMIN_ITEM] : NAV_ITEMS;
  const bottomItems = NAV_ITEMS.filter((i) => i.showInBottomNav);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    // Búsqueda global: de momento lleva al dashboard con el término en query,
    // que es donde vive la tabla de items. Cuando exista una página de
    // resultados dedicada, este es el único sitio que hay que cambiar.
    navigate(`/?q=${encodeURIComponent(search)}`);
  }, [navigate, search]);

  // Atajo de teclado ⌘K / Ctrl+K: enfoca la búsqueda de la topbar
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        document.getElementById('sgc-topbar-search')?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="sgc-shell" data-theme={theme}>
      <aside className="sgc-shell__sidebar">
        <div className="sgc-shell__logo">
          <LogoMark width={24} height={24} aria-hidden="true" />
        </div>
        <nav className="sgc-shell__nav">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `sgc-shell__nav-item ${isActive ? 'sgc-shell__nav-item--active' : ''}`}
              title={t(item.labelKey)}
            >
              <item.icon width={22} height={22} aria-hidden="true" />
              {item.to === '/messages' && unreadMessages > 0 && (
                <span className="sgc-shell__nav-badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button type="button" className="sgc-shell__logout" onClick={onLogout} title={t('nav.logout')}>
          <LogOutIcon width={20} height={20} aria-hidden="true" />
        </button>
      </aside>

      <div className="sgc-shell__main">
        <header className="sgc-shell__topbar">
          <button
            type="button"
            className="sgc-shell__menu-toggle"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={t('nav.menu')}
          >
            <MenuIcon width={22} height={22} aria-hidden="true" />
          </button>

          <form className="sgc-shell__search" onSubmit={handleSearchSubmit} role="search">
            <SearchIcon width={16} height={16} aria-hidden="true" />
            <input
              id="sgc-topbar-search"
              type="search"
              placeholder={t('shell.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <kbd className="sgc-shell__search-kbd">⌘K</kbd>
          </form>

          <div className="sgc-shell__topbar-actions">
            <button type="button" className="sgc-shell__icon-btn" title={t('nav.notifications')}>
              <BellIcon width={18} height={18} aria-hidden="true" />
            </button>
            <button type="button" className="sgc-shell__icon-btn" onClick={() => navigate('/share')} title={t('nav.share')}>
              <ShareIcon width={18} height={18} aria-hidden="true" />
            </button>
            <PillButton icon={PlusIcon} size="sm" onClick={() => navigate('/vault/new')}>
              {t('shell.newItem')}
            </PillButton>
          </div>
        </header>

        {needsSecurityFactor && (
          <div className="sgc-shell__security-banner">
            <LockIcon width={16} height={16} aria-hidden="true" />
            <span>{t('shell.securityBannerTitle')}</span>
            <button type="button" onClick={() => navigate('/account/security')}>
              {t('shell.securityBannerCta')}
            </button>
          </div>
        )}

        <main className="sgc-shell__content">
          <Outlet />
        </main>
      </div>

      {mobileMenuOpen && (
        <div className="sgc-shell__mobile-overlay" onClick={() => setMobileMenuOpen(false)}>
          <nav className="sgc-shell__mobile-menu" onClick={(e) => e.stopPropagation()}>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className="sgc-shell__mobile-menu-item"
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon width={20} height={20} aria-hidden="true" />
                <span>{t(item.labelKey)}</span>
                {item.to === '/messages' && unreadMessages > 0 && (
                  <span className="sgc-shell__nav-badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
                )}
              </NavLink>
            ))}
            <button type="button" className="sgc-shell__mobile-menu-item" onClick={onLogout}>
              {t('nav.logout')}
            </button>
          </nav>
        </div>
      )}

      <nav className="sgc-shell__bottom-nav" aria-label={t('nav.menu')}>
        {bottomItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `sgc-shell__bottom-item ${isActive ? 'sgc-shell__bottom-item--active' : ''}`}
          >
            <item.icon width={20} height={20} aria-hidden="true" />
          </NavLink>
        ))}
        <button type="button" className="sgc-shell__bottom-fab" onClick={() => navigate('/vault/new')} aria-label={t('shell.newItem')}>
          <PlusIcon width={20} height={20} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}

export default AppShell;
